import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { PropertyCard } from '../components/property/PropertyCard';
import { getFavorites } from '../api/client';
import { useNavigate } from 'react-router-dom';

export function FavoritesPage() {
  const { isAuthenticated, isChecking, properties, demands, auctions, setFavorites, favorites } = useAppStore();
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isChecking) return; // Wait for auth check
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }

    const loadFavorites = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await getFavorites();
        if (response.success && response.favorites) {
          // Extract property IDs from favorites
          const favoriteIds = response.favorites.map(fav => String(fav.id || fav.property_id));
          setFavorites(favoriteIds);
        } else {
          setError(response.error || 'Greška pri učitavanju omiljenih oglasa');
        }
      } catch (err) {
        console.error('Error loading favorites:', err);
        setError('Greška pri učitavanju omiljenih oglasa');
      } finally {
        setIsLoading(false);
      }
    };

    loadFavorites();
  }, [isAuthenticated, isChecking, navigate, setFavorites]);

  // Filter properties that are in favorites
  const allProperties = [...properties, ...demands, ...auctions];
  const favoriteProperties = allProperties.filter(p => favorites.includes(p.id));

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            <svg className="w-8 h-8 inline-block mr-2 text-red-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            {language === 'sr' ? 'Omiljeni oglasi' : 'Favorite Listings'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {language === 'sr' 
              ? 'Ovde možete videti sve oglase koje ste sačuvali' 
              : 'Here you can see all the listings you have saved'}
          </p>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center"
          >
            <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </motion.div>
        )}

        {/* Empty State */}
        {!isLoading && !error && favoriteProperties.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center"
          >
            <svg className="w-24 h-24 text-slate-300 dark:text-slate-600 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {language === 'sr' ? 'Nemate omiljenih oglasa' : 'No favorite listings yet'}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {language === 'sr' 
                ? 'Kliknite na srce na bilo kom oglasu da biste ga dodali u omiljene' 
                : 'Click the heart icon on any listing to add it to favorites'}
            </p>
            <button
              onClick={() => navigate('/ponuda')}
              className="px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            >
              {language === 'sr' ? 'Pregledaj oglase' : 'Browse Listings'}
            </button>
          </motion.div>
        )}

        {/* Favorites Grid */}
        {!isLoading && !error && favoriteProperties.length > 0 && (
          <>
            <div className="mb-4 text-slate-600 dark:text-slate-400">
              {language === 'sr' 
                ? `${favoriteProperties.length} ${favoriteProperties.length === 1 ? 'oglas' : favoriteProperties.length < 5 ? 'oglasa' : 'oglasa'}` 
                : `${favoriteProperties.length} ${favoriteProperties.length === 1 ? 'listing' : 'listings'}`}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favoriteProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
